/**
 * Object-based juggling detector using MediaPipe Object Detector (EfficientDet-Lite0, COCO).
 *
 * Why MediaPipe: Runs in-browser via WASM, no server; good balance of speed and accuracy for
 * web. Alternatives: TensorFlow.js + COCO-SSD (similar), ONNX Runtime Web (different models).
 * For "any round thing" we don't use the API allowlist—we get all detections and filter in JS
 * by known round COCO labels (sports ball, orange, apple, etc.) plus a shape heuristic
 * (aspect ratio ~1) for other small round-looking detections.
 */
import {
  ObjectDetector,
  FilesetResolver,
  type Detection as MPDetection,
} from '@mediapipe/tasks-vision';

export interface DetectionResult {
  isJuggling: boolean;
  throwDetected: boolean;
  /** How many throws to count this frame (one per ball that moved up). */
  throwCount: number;
  dropDetected: boolean;
  isCalibrating: boolean;
  ballCount: number;
  /**
   * Current tracked ball states for this frame.
   * Used for path visualization and replay.
   */
  balls: ReadonlyArray<BallState>;
}

interface BallPosition {
  x: number;
  y: number;
}

interface BallTrack {
  id: number;
  x: number;
  y: number;
  prevY: number;
  countedThrowForArc: boolean;
  framesSinceSeen: number;
}

export interface BallState {
  id: number;
  x: number;
  y: number;
  prevY: number;
}

/** COCO labels that are round or round-ish (balls, fruit, small round objects). */
const ROUND_CATEGORIES = new Set([
  'sports ball',
  'orange',
  'apple',
  'donut',
  'bowl',
  'bottle',
  'wine glass',
  'cup',
  'vase',
  'cake',
  'frisbee',
]);

/** Categories we never treat as "round" even if bbox aspect ratio is square. */
const NOT_ROUND = new Set([
  'person',
  'bicycle',
  'car',
  'motorcycle',
  'airplane',
  'bus',
  'train',
  'truck',
  'boat',
  'chair',
  'couch',
  'bed',
  'dining table',
  'tv',
  'laptop',
  'keyboard',
  'cell phone',
  'book',
]);

/** Bbox aspect ratio (w/h) in this range treated as "round enough" for unknown labels. */
const ROUND_ASPECT_MIN = 0.55;
const ROUND_ASPECT_MAX = 1.8;

const WASM_BASE =
  'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm';
const MODEL_URL =
  'https://storage.googleapis.com/mediapipe-models/object_detector/efficientdet_lite0/float16/1/efficientdet_lite0.tflite';

/** Minimum upward movement (px) to count as a throw. At 60fps a fast throw is ~5–10px/frame; keep low. */
const THROW_UP_THRESHOLD = 5;
/** Movement (px) down to reset "counted this arc" so next up counts again. */
const THROW_ARC_RESET_DOWN = 5;
/** Frames with no balls before we declare "pattern stopped" (drop). */
const DROP_FRAMES_THRESHOLD = 18;
/** Max pixel distance to match a detection to an existing track. */
const MATCH_DISTANCE = 80;
/** Min ms between throw events (per-track arc already prevents double count; this just debounces same frame). */
const THROW_COOLDOWN_MS = 35;

export class JugglingDetector {
  private detector: ObjectDetector | null = null;
  private initialized = false;
  private initPromise: Promise<void> | null = null;
  private calibrationFrames: number;
  private frameCount = 0;
  private tracks: BallTrack[] = [];
  private nextTrackId = 1;
  private lastThrowTime = 0;
  private hadBallsRecently = false;
  private framesWithoutBalls = 0;
  private debugLogThrottle = 0;

  constructor(calibrationSeconds = 3) {
    this.calibrationFrames = Math.max(1, Math.floor(calibrationSeconds * 30)); // ~30fps for detection
  }

  async initialize(): Promise<void> {
    if (this.initialized) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = (async () => {
      const vision = await FilesetResolver.forVisionTasks(WASM_BASE);
      this.detector = await ObjectDetector.createFromOptions(vision, {
        baseOptions: { modelAssetPath: MODEL_URL },
        scoreThreshold: 0.2,
        maxResults: 12,
        runningMode: 'VIDEO',
      });
      this.initialized = true;
    })();

    return this.initPromise;
  }

  isReady(): boolean {
    return this.initialized && this.detector != null;
  }

  detect(video: HTMLVideoElement, timestampMs: number): DetectionResult {
    const defaultResult: DetectionResult = {
      isJuggling: false,
      throwDetected: false,
      throwCount: 0,
      dropDetected: false,
      isCalibrating: this.frameCount < this.calibrationFrames,
      ballCount: 0,
      balls: [],
    };

    if (!video.videoWidth || !video.videoHeight) {
      return { ...defaultResult, isCalibrating: true };
    }

    if (!this.detector || !this.initialized) {
      return defaultResult;
    }

    try {
      const result = this.detector.detectForVideo(video, timestampMs);
      const balls = this.filterRoundObjects(result.detections, video.videoWidth, video.videoHeight);
      const positions = balls.map((d) => this.centroid(d));

      if (process.env.NODE_ENV === 'development' && result.detections.length > 0) {
        this.debugLogThrottle++;
        if (this.debugLogThrottle % 60 === 0) {
          const names = result.detections.map((d) => d.categories[0]?.categoryName ?? '?').join(', ');
          console.log('[JugglingDetector] raw:', result.detections.length, 'round:', positions.length, '|', names);
        }
      }

      this.frameCount++;
      const isCalibrating = this.frameCount < this.calibrationFrames;

      if (isCalibrating) {
        return {
          isJuggling: false,
          throwDetected: false,
          throwCount: 0,
          dropDetected: false,
          isCalibrating: true,
          ballCount: positions.length,
          balls: [],
        };
      }

      this.updateTracks(positions);
      const throwCount = this.detectThrows();
      const dropDetected = this.detectDrop(positions.length);
      const isJuggling = this.tracks.length > 0 || positions.length > 0;
      const ballStates: BallState[] = this.tracks.map((t) => ({
        id: t.id,
        x: t.x,
        y: t.y,
        prevY: t.prevY,
      }));

      if (positions.length > 0) {
        this.hadBallsRecently = true;
        this.framesWithoutBalls = 0;
      } else {
        this.framesWithoutBalls++;
      }

      return {
        isJuggling,
        throwDetected: throwCount > 0,
        throwCount,
        dropDetected,
        isCalibrating: false,
        ballCount: positions.length,
        balls: ballStates,
      };
    } catch (err) {
      console.error('JugglingDetector.detect error:', err);
      return defaultResult;
    }
  }

  private filterRoundObjects(
    detections: MPDetection[],
    frameWidth: number,
    frameHeight: number
  ): MPDetection[] {
    return detections.filter((d) => {
      const name = d.categories[0]?.categoryName?.toLowerCase() ?? '';
      if (ROUND_CATEGORIES.has(name)) return true;
      if (NOT_ROUND.has(name)) return false;
      const box = d.boundingBox;
      if (!box || box.width <= 0 || box.height <= 0) return false;
      const aspect = box.width / box.height;
      if (aspect >= ROUND_ASPECT_MIN && aspect <= ROUND_ASPECT_MAX) return true;
      return false;
    });
  }

  private centroid(d: MPDetection): BallPosition {
    const box = d.boundingBox;
    if (!box) return { x: 0, y: 0 };
    return {
      x: box.originX + box.width / 2,
      y: box.originY + box.height / 2,
    };
  }

  private updateTracks(positions: BallPosition[]): void {
    const used = new Set<number>();
    const nextTracks: BallTrack[] = [];

    for (const pos of positions) {
      let bestIdx = -1;
      let bestDist = MATCH_DISTANCE;
      for (let i = 0; i < this.tracks.length; i++) {
        if (used.has(i)) continue;
        const t = this.tracks[i];
        const dx = pos.x - t.x;
        const dy = pos.y - t.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < bestDist) {
          bestDist = dist;
          bestIdx = i;
        }
      }
      if (bestIdx >= 0) {
        used.add(bestIdx);
        const prev = this.tracks[bestIdx];
        nextTracks.push({
          id: prev.id,
          x: pos.x,
          y: pos.y,
          prevY: prev.y,
          countedThrowForArc: prev.countedThrowForArc,
          framesSinceSeen: 0,
        });
      } else {
        nextTracks.push({
          id: this.nextTrackId++,
          x: pos.x,
          y: pos.y,
          prevY: pos.y,
          countedThrowForArc: true,
          framesSinceSeen: 0,
        });
      }
    }

    this.tracks = nextTracks;
  }

  /** Returns number of throws to count this frame (one per track that moved up). */
  private detectThrows(): number {
    const now = Date.now();
    if (now - this.lastThrowTime < THROW_COOLDOWN_MS) return 0;

    let count = 0;
    for (const t of this.tracks) {
      const dy = t.prevY - t.y;
      if (dy <= -THROW_ARC_RESET_DOWN) {
        t.countedThrowForArc = false;
      }
      if (dy >= THROW_UP_THRESHOLD && !t.countedThrowForArc) {
        t.countedThrowForArc = true;
        count++;
      }
    }
    if (count > 0) this.lastThrowTime = now;
    return count;
  }

  private detectDrop(ballCount: number): boolean {
    if (ballCount > 0) return false;
    if (!this.hadBallsRecently) return false;
    if (this.framesWithoutBalls < DROP_FRAMES_THRESHOLD) return false;
    this.hadBallsRecently = false;
    this.framesWithoutBalls = 0;
    return true;
  }

  getDetectionZone(videoWidth: number, videoHeight: number): {
    x: number;
    y: number;
    width: number;
    height: number;
  } {
    const marginX = Math.floor(videoWidth * 0.05);
    const marginY = Math.floor(videoHeight * 0.1);
    return {
      x: marginX,
      y: marginY,
      width: videoWidth - marginX * 2,
      height: Math.floor(videoHeight * 0.85) - marginY,
    };
  }
}
